import React, { useMemo, useState } from "react";

// @material-ui/core
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import GetAppIcon from "@material-ui/icons/GetApp";

// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer";

import {
  Map,
  TileLayer,
  CircleMarker,
  Tooltip,
  //@ts-ignore
} from "react-leaflet";
import SpinLoading from "components/Loading/SpinLoading.js";

import "react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css";
import intl from "react-intl-universal";

import { useRootSelector } from "hooks/reduxHooks";
import Reading from "spectral-core/models/Reading";
import { useUserLoggedIn } from "hooks/userHooks";
import { useGetReadings } from "hooks/readingsHooks";
import { cultureTypeLabelPt } from "spectral-core/cultureEnum";
import { filterNutroScanReadings, parsePredictedValues } from "utils/nutroScanAggregates";
import { exportToExcel } from "utils/excelExport";
import { getDiretoria } from "utils/diretoriaMap";

const useStyles = makeStyles({
  container: { padding: 16 },
  title: { fontSize: 20, fontWeight: 600, color: "#37474f", marginBottom: 12 },
  filters: {
    padding: "12px 16px",
    marginBottom: 12,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
});

const CHART_BAR_COLORS = [
  "#00838f", "#26a69a", "#43a047", "#66bb6a",
  "#8bc34a", "#cddc39", "#ffb300", "#fb8c00",
  "#ef5350", "#ab47bc", "#5c6bc0", "#29b6f6",
];

type ColorByOption = "diretoria" | "usuario" | "cultura";

function getColorByKey(r: Reading, colorBy: ColorByOption): string {
  switch (colorBy) {
    case "diretoria": return getDiretoria(r.userName);
    case "usuario": return (r.userName || "").trim() || "—";
    case "cultura": return cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
  }
}

interface Pin {
  position: [number, number];
  name: string;
  diretoria: string;
  usuario: string;
  culture: string;
  detail: string;
  color: string;
  groupKey: string;
}

export default function Coordinates() {
  const classes = useStyles();

  const isLoggedIn = useUserLoggedIn();
  useGetReadings();

  const rawReadings = useRootSelector((state) => state.reading.readings) as
    | Reading[]
    | undefined;

  const loading = !isLoggedIn || rawReadings === undefined;

  const [filterDiretoria, setFilterDiretoria] = useState<string>("");
  const [filterUser, setFilterUser] = useState<string[]>([]);
  const [filterCulture, setFilterCulture] = useState<string>("");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");
  const [colorBy, setColorBy] = useState<ColorByOption>("diretoria");

  // Só leituras médias (ICL considera média = análise consolidada, mesmo critério das outras abas)
  const baseReadings = useMemo(
    () => (rawReadings ? filterNutroScanReadings(rawReadings, true) : []),
    [rawReadings]
  );

  const availableDiretorias = useMemo(() => {
    const set = new Set<string>();
    for (const r of baseReadings) {
      const d = getDiretoria(r.userName);
      if (d) set.add(d);
    }
    return Array.from(set).sort();
  }, [baseReadings]);

  const afterDiretoria = useMemo(
    () =>
      filterDiretoria
        ? baseReadings.filter((r) => getDiretoria(r.userName) === filterDiretoria)
        : baseReadings,
    [baseReadings, filterDiretoria]
  );

  const availableUsers = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterDiretoria) {
      const u = (r.userName || "").trim();
      if (u) set.add(u);
    }
    return Array.from(set).sort();
  }, [afterDiretoria]);

  const afterUser = useMemo(
    () =>
      filterUser.length > 0
        ? afterDiretoria.filter((r) => filterUser.includes((r.userName || "").trim()))
        : afterDiretoria,
    [afterDiretoria, filterUser]
  );

  const availableCultures = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterUser) {
      const c = cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
      set.add(c);
    }
    return Array.from(set).sort();
  }, [afterUser]);

  // Aplica todos os filtros
  const filteredReadings = useMemo(() => {
    let f = afterUser;
    if (filterCulture) {
      f = f.filter((r) => cultureTypeLabelPt(r.culture, r.customCultureName) === filterCulture);
    }
    if (filterDateFrom) {
      const from = new Date(filterDateFrom + "T00:00:00");
      f = f.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) >= from;
      });
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo + "T23:59:59");
      f = f.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) <= to;
      });
    }
    return f;
  }, [afterUser, filterCulture, filterDateFrom, filterDateTo]);

  // Mapa de cores por dimensão selecionada
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    let idx = 0;
    for (const r of filteredReadings) {
      const key = getColorByKey(r, colorBy);
      if (!map[key]) {
        map[key] = CHART_BAR_COLORS[idx % CHART_BAR_COLORS.length];
        idx++;
      }
    }
    return map;
  }, [filteredReadings, colorBy]);

  // Contagem de leituras por classe (incluindo as sem coordenada)
  const classCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of filteredReadings) {
      const key = getColorByKey(r, colorBy);
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [filteredReadings, colorBy]);

  // Legenda ordenada por contagem desc
  const legendEntries = useMemo(() => {
    return Object.entries(classCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({
        name,
        count,
        color: colorMap[name] || "#9e9e9e",
      }));
  }, [classCounts, colorMap]);

  const pins: Pin[] = useMemo(() => {
    const out: Pin[] = [];
    for (const r of filteredReadings) {
      try {
        if (!r.geoLocation || r.geoLocation.length < 10) continue;
        const { Latitude, Longitude } = JSON.parse(r.geoLocation);
        const culture = cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
        const diretoria = getDiretoria(r.userName);
        const usuario = (r.userName || "").trim() || "—";
        const groupKey = getColorByKey(r, colorBy);
        const preds = parsePredictedValues(r);
        const detail = preds
          .slice(0, 5)
          .map((p) => `${p.ParameterName}: ${p.Value?.toFixed?.(2) ?? p.Value}`)
          .join(", ");
        out.push({
          position: [Latitude, Longitude],
          name: r.name || r.sampleCode || "—",
          diretoria,
          usuario,
          culture,
          detail,
          groupKey,
          color: colorMap[groupKey] || "#9e9e9e",
        });
      } catch {
        /* skip */
      }
    }
    return out;
  }, [filteredReadings, colorMap, colorBy]);

  if (loading) {
    return <SpinLoading />;
  }

  return (
    <div>
      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12}>
          <Paper className={classes.container}>
            <Typography className={classes.title}>
              {intl.get("coordinate.coordinate")}
            </Typography>

            {/* Filtros */}
            <Paper className={classes.filters} elevation={0}>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="coord-filter-diretoria" shrink>Diretoria</InputLabel>
                <Select
                  labelId="coord-filter-diretoria"
                  value={filterDiretoria}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFilterDiretoria(e.target.value as string);
                    setFilterUser([]);
                    setFilterCulture("");
                  }}
                  displayEmpty
                >
                  <MenuItem value="">Todas</MenuItem>
                  {availableDiretorias.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="coord-filter-user" shrink>Usuário</InputLabel>
                <Select
                  labelId="coord-filter-user"
                  multiple
                  value={filterUser}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFilterUser(e.target.value as string[]);
                    setFilterCulture("");
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    const sel = selected as string[];
                    if (sel.length === 0) return "Todos";
                    if (sel.length === 1) return sel[0];
                    return `${sel.length} usuários`;
                  }}
                >
                  {availableUsers.map((u) => (
                    <MenuItem key={u} value={u}>
                      <Checkbox checked={filterUser.indexOf(u) > -1} size="small" color="primary" />
                      <ListItemText primary={u} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
                <InputLabel id="coord-filter-culture" shrink>Cultura</InputLabel>
                <Select
                  labelId="coord-filter-culture"
                  value={filterCulture}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    setFilterCulture(e.target.value as string)
                  }
                  displayEmpty
                >
                  <MenuItem value="">Todas</MenuItem>
                  {availableCultures.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="De"
                type="date"
                size="small"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 145, flex: "0 0 145px" }}
              />
              <TextField
                label="Até"
                type="date"
                size="small"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ width: 145, flex: "0 0 145px" }}
              />
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                style={{ marginLeft: "auto" }}
                onClick={() =>
                  exportToExcel(
                    pins.map((p) => ({
                      nome: p.name,
                      diretoria: p.diretoria,
                      usuario: p.usuario,
                      cultura: p.culture,
                      latitude: p.position[0],
                      longitude: p.position[1],
                      detalhe: p.detail,
                    })),
                    "Coordenadas",
                    "Coordenadas",
                    [
                      { header: "Nome", field: "nome" },
                      { header: "Diretoria", field: "diretoria" },
                      { header: "Usuário", field: "usuario" },
                      { header: "Cultura", field: "cultura" },
                      { header: "Latitude", field: "latitude" },
                      { header: "Longitude", field: "longitude" },
                      { header: "Parâmetros", field: "detalhe" },
                    ]
                  )
                }
              >
                Exportar Excel
              </Button>
            </Paper>

            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
              <FormControl size="small" style={{ minWidth: 140 }}>
                <InputLabel id="coord-color-by">Legenda</InputLabel>
                <Select
                  labelId="coord-color-by"
                  value={colorBy}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    setColorBy(e.target.value as ColorByOption)
                  }
                >
                  <MenuItem value="diretoria">Diretoria</MenuItem>
                  <MenuItem value="usuario">Usuário</MenuItem>
                  <MenuItem value="cultura">Cultura</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                {pins.length} ponto(s) com coordenadas — {filteredReadings.length} leitura(s) total
              </Typography>
            </div>

            {/* Mapa + Legenda lado a lado */}
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              {/* Mapa */}
              <div style={{ flex: 1, height: "65vh", minWidth: 0 }}>
                {pins.length ? (
                  <Map
                    center={pins[0].position as [number, number]}
                    zoom={5}
                    style={{ height: "100%", borderRadius: 4 }}
                  >
                    <TileLayer
                      url={`https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=cb1_3vnf_2_b4ced25f80bd977161a67037`}
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    />
                    {pins.map((pin, i) => (
                      <CircleMarker
                        key={i}
                        center={pin.position}
                        radius={6}
                        fillOpacity={0.7}
                        color={pin.color}
                        fillColor={pin.color}
                      >
                        <Tooltip direction="top" offset={[-8, -2]} opacity={1}>
                          <span>{pin.name}</span>
                          <br />
                          <span>{pin.culture}</span>
                          {pin.detail && (
                            <>
                              <br />
                              <span>{pin.detail}</span>
                            </>
                          )}
                        </Tooltip>
                      </CircleMarker>
                    ))}
                  </Map>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
                    Sem coordenadas nas leituras carregadas.
                  </Typography>
                )}
              </div>

              {/* Legenda à direita */}
              {legendEntries.length > 0 && (
                <div style={{
                  width: 220,
                  flexShrink: 0,
                  maxHeight: "65vh",
                  overflowY: "auto",
                  paddingRight: 4,
                }}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600, color: "#37474f" }}>
                    {colorBy === "diretoria" ? "Diretoria" : colorBy === "usuario" ? "Usuário" : "Cultura"}
                  </Typography>
                  {legendEntries.map((entry) => (
                    <div
                      key={entry.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                        fontSize: 13,
                        lineHeight: 1.3,
                      }}
                    >
                      <span style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: entry.color,
                        display: "inline-block",
                        flexShrink: 0,
                      }} />
                      <span style={{ fontWeight: 500 }}>{entry.name}</span>
                      <span style={{ color: "#666", whiteSpace: "nowrap" }}>({entry.count})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Paper>
        </GridItem>
      </GridContainer>
    </div>
  );
}
